import type {Appareil} from "@mytour/shared";
import {
  APPAREIL_TYPES,
  ARRIVEE_AIR_OPTIONS,
  COMBUSTIBLE_MAP,
  CONDUIT_TYPES,
  EMPLACEMENTS,
  refineCustomAppareilType,
} from "@mytour/shared";
import {FloatingField} from "@mytour/ui/form";
import {Splitter} from "@mytour/ui/layout";
import type {Control, FieldErrors, UseFormSetValue, UseFormWatch} from "react-hook-form";
import {Controller} from "react-hook-form";
import {FormGroup, Input, Label} from "reactstrap";
import {z} from "zod";

export const applianceSchema = z
  .object({
    appareilType: z.string().min(1, "Requis"),
    appareilTypeCustom: z.string().optional(),
    appareilEmplacement: z.string().min(1, "Requis"),
    combustible: z.string().min(1, "Requis"),
    conduitType: z.string().min(1, "Requis"),
    arriveeAir: z.string().min(1, "Requis"),
  })
  .superRefine(refineCustomAppareilType);

export type ApplianceFormData = z.infer<typeof applianceSchema>;

type Props = {
  control: Control<ApplianceFormData>;
  errors: FieldErrors<ApplianceFormData>;
  disabled: boolean;
  watch: UseFormWatch<ApplianceFormData>;
  setValue: UseFormSetValue<ApplianceFormData>;
};

export function ApplianceForm({control, errors, disabled, watch, setValue}: Props) {
  const appareilType = watch("appareilType");

  const combustible = watch("combustible");

  return (
    <>
      <h5 style={{marginBottom: "16px"}}>Appareil</h5>
      <Splitter>
        <FormGroup floating>
          <Controller
            name="appareilType"
            control={control}
            render={({field}) => (
              <Input
                {...field}
                value={
                  COMBUSTIBLE_MAP[appareilType]
                    ? appareilType
                    : appareilType
                      ? `${combustible}|${appareilType}`
                      : ""
                }
                onChange={(event) => {
                  const selected = event.target.value;
                  const [category, type] = COMBUSTIBLE_MAP[selected]
                    ? [COMBUSTIBLE_MAP[selected], selected]
                    : selected.split("|");
                  field.onChange(type ?? "");
                  setValue("combustible", category ?? "");
                  setValue("appareilTypeCustom", "");
                }}
                type="select"
                id="field-appareilType"
                placeholder="Type d'appareil"
                invalid={!!errors.appareilType}
                disabled={disabled}
              >
                <option value="">--</option>
                {APPAREIL_TYPES.map((group) => (
                  <optgroup key={group.category} label={group.category}>
                    {group.items.map((item) => (
                      <option
                        key={item}
                        value={COMBUSTIBLE_MAP[item] ? item : `${group.category}|${item}`}
                      >
                        {item}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </Input>
            )}
          />
          <Label for="field-appareilType">Type d&apos;appareil</Label>
        </FormGroup>

        <FloatingField
          name="combustible"
          label="Combustible"
          control={control}
          error={errors.combustible}
          disabled={true}
        />
      </Splitter>

      {appareilType === "Autre" && (
        <FloatingField
          name="appareilTypeCustom"
          label="Précisez le type d'appareil"
          control={control}
          error={errors.appareilTypeCustom}
          disabled={disabled}
        />
      )}

      <FormGroup floating>
        <Controller
          name="appareilEmplacement"
          control={control}
          render={({field}) => (
            <Input
              {...field}
              type="select"
              id="field-appareilEmplacement"
              placeholder="Emplacement"
              invalid={!!errors.appareilEmplacement}
              disabled={disabled}
            >
              <option value="">--</option>
              {EMPLACEMENTS.map((e) => (
                <option key={e} value={e}>
                  {e}
                </option>
              ))}
            </Input>
          )}
        />
        <Label for="field-appareilEmplacement">Emplacement</Label>
      </FormGroup>
      <FormGroup floating>
        <Controller
          name="conduitType"
          control={control}
          render={({field}) => (
            <Input
              {...field}
              type="select"
              id="field-conduitType"
              placeholder="Type de conduit"
              invalid={!!errors.conduitType}
              disabled={disabled}
            >
              <option value="">--</option>
              {CONDUIT_TYPES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </Input>
          )}
        />
        <Label for="field-conduitType">Type de conduit</Label>
      </FormGroup>

      <FormGroup floating>
        <Controller
          name="arriveeAir"
          control={control}
          render={({field}) => (
            <Input
              {...field}
              type="select"
              id="field-arriveeAir"
              placeholder="Arrivée d'air"
              invalid={!!errors.arriveeAir}
              disabled={disabled}
            >
              <option value="">--</option>
              {ARRIVEE_AIR_OPTIONS.map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </Input>
          )}
        />
        <Label for="field-arriveeAir">Arrivée d&apos;air</Label>
      </FormGroup>
    </>
  );
}

export function getApplianceDefaultValues(appareil: Appareil): ApplianceFormData {
  return {
    appareilType: appareil.appareilType,
    appareilTypeCustom: appareil.appareilTypeCustom ?? "",
    appareilEmplacement: appareil.appareilEmplacement,
    combustible: appareil.combustible,
    conduitType: appareil.conduitType,
    arriveeAir: appareil.arriveeAir,
  };
}
